import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import {
	Button,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import { cn } from '@/lib/utils';

type EntityOption = { id: string; name: string };

type EntityChildCreateModalProps = {
	isOpen: boolean;
	onClose: () => void;
	options: EntityOption[];
	defaultOptionId?: string;
	title: string;
	description: string;
	selectLabel: string;
	selectPlaceholder: string;
	childLabel: string;
	childPlaceholder: string;
	submitText: string;
	selectId: string;
	inputId: string;
	onCreate: (entityId: string, childName: string) => void;
};

const labelClass =
	'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-300';
const inputFieldClass = cn(
	'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm font-bold text-stone-600 outline-none transition-all box-border',
	'border-stone-200 focus:border-stone-400 focus:bg-white',
);

export const EntityChildCreateModal = ({
	isOpen,
	onClose,
	options,
	defaultOptionId = '',
	title,
	description,
	selectLabel,
	selectPlaceholder,
	childLabel,
	childPlaceholder,
	submitText,
	selectId,
	inputId,
	onCreate,
}: EntityChildCreateModalProps) => {
	const [entityId, setEntityId] = useState('');
	const [childName, setChildName] = useState('');

	useEffect(() => {
		if (isOpen) {
			setChildName('');
			setEntityId(
				defaultOptionId &&
					options.some((item) => item.id === defaultOptionId)
					? defaultOptionId
					: options[0]?.id ?? '',
			);
		}
	}, [isOpen, defaultOptionId, options]);

	const selectedLabel =
		options.find((item) => item.id === entityId)?.name ?? '';

	if (!isOpen) return null;

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		const trimmed = childName.trim();
		if (!entityId || !trimmed) return;
		onCreate(entityId, trimmed);
		onClose();
	};

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
				aria-hidden
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<form
					onSubmit={handleSubmit}
					noValidate
					className="flex flex-col overflow-hidden"
				>
					<div className="flex shrink-0 items-start justify-between gap-4 px-8 pb-4 pt-8">
						<div className="min-w-0 space-y-1">
							<h2 className="text-lg font-black text-stone-900">
								{title}
							</h2>
							<p className="text-sm text-stone-500">
								{description}
							</p>
						</div>
						<button
							type="button"
							onClick={onClose}
							className="shrink-0 text-stone-400 transition-colors hover:text-stone-600"
							aria-label="Fechar"
						>
							<X size={20} />
						</button>
					</div>

					<div className="flex-1 space-y-6 overflow-y-auto px-8 pb-4 pt-0">
						<div>
							<label className={labelClass} htmlFor={selectId}>
								{selectLabel}
							</label>
							<Select
								value={entityId}
								onValueChange={(value) => setEntityId(value)}
								disabled={options.length === 0}
							>
								<SelectTrigger
									id={selectId}
									className={cn(
										'h-auto min-h-[3rem] w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm font-bold text-stone-600 shadow-none',
										'focus:border-stone-400 focus:outline-none focus:ring-0',
									)}
								>
									<SelectValue
										className={
											selectedLabel
												? 'text-stone-600'
												: 'text-stone-500'
										}
									>
										{selectedLabel || selectPlaceholder}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									{options.map((item) => (
										<SelectItem
											key={item.id}
											value={item.id}
										>
											{item.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div>
							<label className={labelClass} htmlFor={inputId}>
								{childLabel}
							</label>
							<input
								id={inputId}
								type="text"
								value={childName}
								onChange={(e) => setChildName(e.target.value)}
								placeholder={childPlaceholder}
								className={inputFieldClass}
							/>
						</div>
					</div>

					<div className="flex shrink-0 items-center justify-end gap-6 px-8 pb-8 pt-4">
						<button
							type="button"
							onClick={onClose}
							className="text-sm font-bold text-stone-500 hover:text-stone-700"
						>
							Cancelar
						</button>
						<Button
							type="submit"
							text={submitText}
							disabled={
								!entityId.trim() ||
								!childName.trim() ||
								options.length === 0
							}
							className="min-w-[140px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800 disabled:cursor-not-allowed disabled:bg-stone-300"
						/>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	);
};
